import { m } from 'framer-motion'
import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { backdropVariants, modalPanelVariants } from '@/shared/motion'

import { useModalA11y } from './useModalA11y'

interface ModalProps {
  onClose: () => void
  /** id del título dentro de `children` → `aria-labelledby`. */
  labelledBy?: string
  /** Alternativa a `labelledBy` cuando no hay un título con id. */
  ariaLabel?: string
  /** Ancho del panel. `md` = max-w-md (default), `lg` = max-w-lg. */
  size?: 'md' | 'lg'
  /** Bloquea cierre por ESC + backdrop (p.ej. mutación en curso). */
  closeDisabled?: boolean
  children: ReactNode
}

const SIZE_CLASS = {
  md: 'max-w-md',
  lg: 'max-w-lg',
} as const

/**
 * Wrapper base de modales: `createPortal` a `document.body`, `role="dialog"` +
 * `aria-modal`, backdrop clickable y el panel brutalist estándar. Centraliza la
 * a11y vía {@link useModalA11y} (ESC, focus-trap, scroll-lock, foco inicial y
 * restauración del foco).
 *
 * El caller monta `<Modal>` solo cuando el modal debe verse (condicional en el
 * padre, o `if (!open) return null` antes de renderizarlo). `children` es el
 * contenido interno del panel — el chrome (overlay/backdrop/panel) lo pone el
 * wrapper.
 *
 * Casos con animación de salida (p.ej. `SessionPreviewModal`) usan
 * `useModalA11y` directamente en vez de este wrapper.
 */
export function Modal({
  onClose,
  labelledBy,
  ariaLabel,
  size = 'md',
  closeDisabled = false,
  children,
}: ModalProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  useModalA11y(panelRef, { onClose, closeEnabled: !closeDisabled })

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
    >
      <m.button
        type="button"
        aria-label={t('common.close')}
        onClick={() => !closeDisabled && onClose()}
        className="absolute inset-0 bg-foreground/60"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
      />
      {/* Entrada "pop" brutal. La salida no se anima porque el caller desmonta
          `<Modal>` (patrón `{open && <Modal/>}`); la entrada es el 90% del
          efecto percibido. `m.div` reenvía el ref al nodo real para la a11y. */}
      <m.div
        ref={panelRef}
        tabIndex={-1}
        className={`brutal-lg relative max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto overscroll-contain ${SIZE_CLASS[size]} rounded-2xl bg-background p-5 focus:outline-none sm:p-6`}
        variants={modalPanelVariants}
        initial="hidden"
        animate="visible"
      >
        {children}
      </m.div>
    </div>,
    document.body,
  )
}
